import React, { FC } from 'react';
import { CustomInput, ListDropDown } from 'components';
import { InputType } from 'components/custom-input.tsx/CustomInput';
import ReminderModalBase from './ReminderModalBase';
import { BaseReminderModalProps, ReminderColumnConfig, ReminderConfig, ScheduleRelation } from './types';
import { ReminderDateTypeLabel } from 'containers/settings/constants';

export interface SettingsReminderModalProps extends BaseReminderModalProps {
  dateType?: ReminderDateTypeLabel;
}

const SettingsReminderModal: FC<SettingsReminderModalProps> = ({
  title = 'Estimate reminders',
  subtitle = 'Configure default reminder settings for estimates',
  customColumns = [],
  dateType = ReminderDateTypeLabel.DUE_DATE,
  ...props
}) => {
  const scheduleRelationsPriority: ScheduleRelation[] = [
    ScheduleRelation.BEFORE,
    ScheduleRelation.ON,
    ScheduleRelation.AFTER
  ];

  // Compute schedule relation options for dropdown
  const scheduleRelationOptions = scheduleRelationsPriority.map(relation => {
    let name = '';
    if (relation === ScheduleRelation.BEFORE) {
      name = `Before ${dateType}`;
    } else if (relation === ScheduleRelation.ON) {
      name = `On ${dateType}`;
    } else {
      name = `After ${dateType}`;
    }
    return { id: relation, name };
  });

  const handleScheduleRelationChange = (
    reminder: ReminderConfig,
    newRelation: ScheduleRelation,
    onUpdate: (field: string, value: unknown) => void
  ) => {
    onUpdate('scheduleRelation', newRelation);

    // Determine new days value based on relation
    let newDays = reminder.days;
    if (newRelation === ScheduleRelation.ON) {
      newDays = 0;
    } else if (reminder.days === 0) {
      newDays = 1;
    }

    // Update days if it changed
    if (newDays !== reminder.days) {
      onUpdate('days', newDays);
    }
  };

  const handleDaysChange = (
    reminder: ReminderConfig,
    value: string,
    onUpdate: (field: string, value: unknown) => void
  ) => {
    const isOnDateSelected = reminder.scheduleRelation === ScheduleRelation.ON;

    // For ON relation, days is always 0
    if (isOnDateSelected) {
      onUpdate('days', 0);
      return;
    }

    // For BEFORE/AFTER: Allow empty string for clearing the field (temporarily set to 0 for display)
    if (value === '' || value === null || value === undefined) {
      onUpdate('days', 0);
      return;
    }

    const numValue = parseInt(value);
    // Prevent 0 and negative values, only allow positive numbers for BEFORE/AFTER
    if (!isNaN(numValue) && numValue > 0) {
      onUpdate('days', numValue);
    }
  };

  const handleDaysBlur = (reminder: ReminderConfig, onUpdate: (field: string, value: unknown) => void) => {
    const isBeforeOrAfter =
      reminder.scheduleRelation === ScheduleRelation.BEFORE || reminder.scheduleRelation === ScheduleRelation.AFTER;

    // On blur, if value is 0 for BEFORE/AFTER (empty/cleared), set to minimum 1
    if (isBeforeOrAfter && reminder.days === 0) {
      onUpdate('days', 1);
    }
  };

  // Default columns for settings mode
  const defaultColumns: ReminderColumnConfig[] = [
    {
      key: 'send',
      label: 'Send',
      width: '200px',
      render: (reminder: ReminderConfig, onUpdate) => (
        <ListDropDown
          id={`schedule-relation-${reminder.id}`}
          selectedItem={scheduleRelationOptions.find(option => option.id === reminder.scheduleRelation)}
          items={scheduleRelationOptions}
          setSelectedItem={item => {
            handleScheduleRelationChange(reminder, item.id as ScheduleRelation, onUpdate);
          }}
          buttonStyles="bg-highlightGray px-3 py-2.5 text-sbase h-10"
          iconStyle="right-3"
          optionStyles="min-w-fit"
          isPortal={true}
        />
      )
    },
    {
      key: 'days',
      label: 'Days',
      width: '110px',
      render: (reminder: ReminderConfig, onUpdate) => {
        const isOnDateSelected = reminder.scheduleRelation === ScheduleRelation.ON;
        // Display empty string when days is 0 for BEFORE/AFTER (allows clearing), but show 0 for ON
        const displayValue = isOnDateSelected || reminder.days > 0 ? reminder.days.toString() : '';
        return (
          <CustomInput
            id={`days-${reminder.id}`}
            type={InputType.NUMBER}
            disabled={isOnDateSelected}
            value={displayValue}
            onChange={value => handleDaysChange(reminder, value, onUpdate)}
            onBlur={() => handleDaysBlur(reminder, onUpdate)}
            className="h-10 w-full px-3 py-2.5"
            inputStyle={`${isOnDateSelected ? 'opacity-60' : ''}`}
            placeholder=""
          />
        );
      }
    }
  ];

  // Merge default columns with custom columns
  const allColumns = [...defaultColumns, ...customColumns];

  return (
    <ReminderModalBase
      {...props}
      title={title}
      subtitle={subtitle}
      columns={allColumns}
      scheduleRelationsPriority={scheduleRelationsPriority}
    />
  );
};

export default SettingsReminderModal;
